import {
  Anvil,
  ArrowRight,
  Brain,
  ChevronRight,
  CircleSlash,
  HeartPulse,
  Leaf,
  PackageOpen,
  Shield,
  Sparkles,
  Sword,
} from "lucide-react";
import { useEffect, useState } from "react";
import { DetailWindow } from "../DetailWindow";
import {
  type InventoryView,
  type LifeProfessionId,
  type LifeProfile,
  type LifeStartRequest,
  type MeResponse,
} from "../types";
import { formatDisplayNumber, formatRunDuration } from "../ui/format";
import { ResourceIcon, type ResourceKind } from "../ui/Resources";
import {
  lifeProfessionCompactBenefit,
  LifeProfessionIcon,
  lifeProfessionMetrics,
  lifeResearchExperience,
  ProbabilityColumn,
} from "./lifePresentation";

export const LIFE_COMPLETION_LABELS: Record<string, string> = {
  idle_limit: "已达 24 小时上限",
  storage_full: "仓库已满",
  resources_exhausted: "资源已用尽",
  target_reached: "目标已达成",
  item_missing: "装备不可用",
};

export function LifeScreen({
  profile,
  inventory,
  me,
  clockNow,
  busy,
  initialItemId,
  onStart,
  onClaim,
  onStop,
  onRefresh,
}: {
  profile: LifeProfile;
  inventory: InventoryView;
  me: MeResponse;
  clockNow: () => number;
  busy: boolean;
  initialItemId: string;
  onStart: (request: LifeStartRequest) => void;
  onClaim: () => void;
  onStop: () => void;
  onRefresh: () => Promise<void>;
}) {
  const [selected, setSelected] = useState<LifeProfessionId>(
    () => profile.activity?.professionId ?? (initialItemId ? "enhancement" : "gathering"),
  );
  const [workWindowOpen, setWorkWindowOpen] = useState(Boolean(initialItemId));
  const [itemId, setItemId] = useState(
    () =>
      initialItemId ||
      profile.activity?.targetItemId ||
      inventory.items.find((item) => item.canEnhance)?.id ||
      "",
  );
  const [enhancementMode, setEnhancementMode] = useState<"attempts" | "target_level">("attempts");
  const [targetValue, setTargetValue] = useState("10");
  const [primaryAffix, setPrimaryAffix] = useState(
    () => profile.researchFocus.primaryAffixId || profile.affixOptions[0]?.id || "",
  );
  const [secondaryAffix, setSecondaryAffix] = useState(
    () =>
      profile.researchFocus.secondaryAffixId ||
      profile.affixOptions[1]?.id ||
      profile.affixOptions[0]?.id ||
      "",
  );
  const [clock, setClock] = useState(() => clockNow());
  const selectedItem = inventory.items.find((item) => item.id === itemId);
  const activeProfession = profile.activity
    ? profile.professions.find((entry) => entry.id === profile.activity?.professionId)
    : undefined;
  const refreshInterval = activeProfession
    ? Math.min(60_000, Math.max(30_000, activeProfession.cycleSeconds * 1000))
    : 60_000;
  useEffect(() => {
    const update = () => setClock(clockNow());
    update();
    const timer = window.setInterval(update, 1_000);
    return () => window.clearInterval(timer);
  }, [clockNow, profile.serverNow]);
  useEffect(() => {
    if (busy) return;
    let active = true;
    const refresh = () => {
      if (!active || document.visibilityState !== "visible") return;
      void onRefresh().catch(() => undefined);
    };
    refresh();
    const timer = profile.activity ? window.setInterval(refresh, refreshInterval) : 0;
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      active = false;
      if (timer) window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [busy, onRefresh, refreshInterval, profile.activity?.id, profile.activity?.status]);
  useEffect(() => {
    if (!initialItemId) return;
    setSelected("enhancement");
    setItemId(initialItemId);
    setWorkWindowOpen(true);
  }, [initialItemId]);
  useEffect(() => {
    setPrimaryAffix(profile.researchFocus.primaryAffixId || profile.affixOptions[0]?.id || "");
    setSecondaryAffix(
      profile.researchFocus.secondaryAffixId ||
        profile.affixOptions[1]?.id ||
        profile.affixOptions[0]?.id ||
        "",
    );
  }, [profile.researchFocus.primaryAffixId, profile.researchFocus.secondaryAffixId]);
  useEffect(() => {
    if (enhancementMode !== "target_level" || !selectedItem) return;
    const minimum = selectedItem.enhancementLevel + 1;
    const maximum = selectedItem.enhancementMaxLevel;
    setTargetValue((value) =>
      String(Math.min(maximum, Math.max(minimum, Math.floor(Number(value) || minimum)))),
    );
  }, [
    enhancementMode,
    selectedItem?.id,
    selectedItem?.enhancementLevel,
    selectedItem?.enhancementMaxLevel,
  ]);
  const profession =
    profile.professions.find((entry) => entry.id === selected) ?? profile.professions[0];
  const activityProfession = profile.activity
    ? profile.professions.find((entry) => entry.id === profile.activity?.professionId)
    : undefined;
  const progress =
    profession?.nextLevelExperience > 0
      ? (profession.levelExperience / profession.nextLevelExperience) * 100
      : 0;
  const resources = profile.activity
    ? Object.entries(profile.activity.claimable).filter(([, amount]) => amount !== 0)
    : [];
  const metrics = profession
    ? lifeProfessionMetrics(
        profession,
        profile,
        inventory,
        me,
        selectedItem,
        primaryAffix,
        secondaryAffix,
      )
    : [];
  const activityComplete = profile.activity
    ? profile.activity.status === "completed" || clock >= profile.activity.deadlineAt
    : false;
  const activityStatus = profile.activity
    ? profile.activity.professionId === profession?.id
      ? activityComplete
        ? (LIFE_COMPLETION_LABELS[profile.activity.completionReason ?? ""] ?? "等待结算")
        : `进行中 · 剩余 ${formatRunDuration(Math.max(0, profile.activity.deadlineAt - clock))}`
      : `当前工作：${activityProfession?.name ?? "生活职业"} · ${activityComplete ? (LIFE_COMPLETION_LABELS[profile.activity.completionReason ?? ""] ?? "等待结算") : `剩余 ${formatRunDuration(Math.max(0, profile.activity.deadlineAt - clock))}`}`
    : "未安排工作";
  const start = () => {
    if (selected === "enhancement") {
      const entered = Math.floor(Number(targetValue) || 1);
      const normalizedTarget =
        enhancementMode === "target_level" && selectedItem
          ? Math.min(
              selectedItem.enhancementMaxLevel,
              Math.max(selectedItem.enhancementLevel + 1, entered),
            )
          : Math.min(2880, Math.max(1, entered));
      onStart({
        professionId: selected,
        targetItemId: itemId,
        mode: enhancementMode,
        targetValue: normalizedTarget,
      });
      return;
    }
    if (selected === "research") {
      onStart({
        professionId: selected,
        primaryAffixId: primaryAffix,
        secondaryAffixId: secondaryAffix,
      });
      return;
    }
    onStart({ professionId: selected });
  };
  const independentWork = selected === "enhancement" || selected === "research";
  const workDetails = profession && (
    <section
      className={`life-active${activityComplete && profile.activity?.professionId === profession.id ? " is-complete" : ""}`}
      aria-label="生活工作详情"
    >
      <div className="life-active-heading">
        <span className="life-profession-icon">
          <LifeProfessionIcon id={profession.id} size={25} />
        </span>
        <div>
          <h2>{profession.name}</h2>
          <small>
            Lv.{profession.level} · {profession.levelExperience}/{profession.nextLevelExperience} XP
          </small>
        </div>
        <strong className="life-active-status">{activityStatus}</strong>
      </div>
      <div className="life-level-progress">
        <i style={{ width: `${Math.min(100, progress)}%` }} />
      </div>
      <div className="life-metrics">
        {metrics.map((metric) => (
          <span key={metric.label}>
            <small>{metric.label}</small>
            <b>{metric.value}</b>
          </span>
        ))}
      </div>
      {profile.activity && (
        <div className="life-active-result">
          <div className="life-active-yield">
            {resources.length === 0 ? (
              <span>待结算</span>
            ) : (
              resources.map(([kind, amount]) => (
                <span key={kind}>
                  <ResourceIcon kind={kind as ResourceKind} size={15} />
                  <b>
                    {amount > 0 ? "+" : ""}
                    {amount}
                  </b>
                </span>
              ))
            )}
            <span>
              <Sparkles size={15} />
              <b>+{profile.activity.claimableExperience} XP</b>
            </span>
            {profile.activity.professionId === "research" && (
              <>
                <span>
                  <Brain size={15} />
                  <b>主 +{profile.activity.claimablePrimaryResearchExperience ?? 0} XP</b>
                </span>
                <span>
                  <Brain size={15} />
                  <b>副 +{profile.activity.claimableSecondaryResearchExperience ?? 0} XP</b>
                </span>
              </>
            )}
            {profile.activity.professionId === "enhancement" && (
              <span>
                <Anvil size={15} />
                <b>
                  +{profile.activity.itemLevelBefore} → +{profile.activity.itemLevelAfter}
                </b>
              </span>
            )}
          </div>
          <div className="life-active-actions">
            <button className="primary-command compact" disabled={busy} onClick={onClaim}>
              <PackageOpen size={16} />
              领取
            </button>
            <button className="secondary-command compact" disabled={busy} onClick={onStop}>
              <CircleSlash size={16} />
              停止
            </button>
          </div>
        </div>
      )}
      {selected === "conversion" && (
        <div className="life-recipe">
          <ResourceIcon kind="wood" size={18} />
          <b>20</b>
          <span>+</span>
          <ResourceIcon kind="ore" size={18} />
          <b>20</b>
          <ArrowRight size={16} />
          <ResourceIcon kind="crystal" size={18} />
          <b>1</b>
        </div>
      )}
      {selected === "enhancement" && (
        <div className="life-controls life-enhancement-controls">
          <label>
            <span>装备</span>
            <select value={itemId} onChange={(event) => setItemId(event.currentTarget.value)}>
              {inventory.items
                .filter((item) => item.canEnhance || item.id === profile.activity?.targetItemId)
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} +{item.enhancementLevel}
                  </option>
                ))}
            </select>
          </label>
          <div className="segmented-control" aria-label="强化目标">
            <button
              type="button"
              className={enhancementMode === "attempts" ? "is-active" : ""}
              onClick={() => setEnhancementMode("attempts")}
            >
              按次数
            </button>
            <button
              type="button"
              className={enhancementMode === "target_level" ? "is-active" : ""}
              onClick={() => setEnhancementMode("target_level")}
            >
              到等级
            </button>
          </div>
          <label>
            <span>{enhancementMode === "attempts" ? "尝试次数" : "目标等级"}</span>
            <input
              type="number"
              min={enhancementMode === "attempts" ? 1 : (selectedItem?.enhancementLevel ?? 0) + 1}
              max={
                enhancementMode === "attempts" ? 2880 : (selectedItem?.enhancementMaxLevel ?? 20)
              }
              value={targetValue}
              onChange={(event) => setTargetValue(event.currentTarget.value)}
            />
          </label>
          {selectedItem && (
            <div className="life-enhance-preview">
              <span>
                <small>当前</small>
                <b>+{selectedItem.enhancementLevel}</b>
              </span>
              <ArrowRight size={16} />
              <span>
                <small>成功率</small>
                <b>{selectedItem.enhanceSuccessPercent}%</b>
              </span>
              <span>
                <small>单次成本</small>
                <b>{selectedItem.enhanceCost.gold} 金</b>
              </span>
              <span>
                <small>失败结果</small>
                <b>
                  {selectedItem.enhanceFailureLevelLoss > 0
                    ? `降 ${selectedItem.enhanceFailureLevelLoss} 级`
                    : "不降级"}
                </b>
              </span>
            </div>
          )}
        </div>
      )}
      {selected === "research" && (
        <div className="life-controls research-focus-controls">
          <div className="life-focus-summary">
            <span>
              <small>主研究</small>
              <b>{lifeResearchExperience(profile, primaryAffix, "primary")} XP</b>
            </span>
            <span>
              <small>副研究</small>
              <b>{lifeResearchExperience(profile, secondaryAffix, "secondary")} XP</b>
            </span>
          </div>
          <label>
            <span>主词条</span>
            <select
              value={primaryAffix}
              onChange={(event) => setPrimaryAffix(event.currentTarget.value)}
            >
              {profile.affixOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>副词条</span>
            <select
              value={secondaryAffix}
              onChange={(event) => setSecondaryAffix(event.currentTarget.value)}
            >
              {profile.affixOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>
          <div className="life-probability-grid">
            <ProbabilityColumn
              title="主词条分布"
              values={profile.primaryProbabilities}
              focused={primaryAffix}
            />
            <ProbabilityColumn
              title="副词条分布"
              values={profile.secondaryProbabilities}
              focused={secondaryAffix}
            />
          </div>
        </div>
      )}
      <button
        className="primary-command life-start"
        disabled={
          busy ||
          (selected === "enhancement" && !selectedItem) ||
          (selected === "research" && (!primaryAffix || !secondaryAffix))
        }
        onClick={start}
      >
        <Leaf size={17} />
        {profile.activity ? "结算并切换" : "开始"}
      </button>
    </section>
  );
  return (
    <section className="life-screen" aria-labelledby="life-title">
      <header className="life-header">
        <div>
          <h1 id="life-title">生活工作</h1>
          <span>{profile.maxIdleHours}h</span>
        </div>
        <div className="life-attribute-strip">
          <span>
            <HeartPulse size={15} />
            {formatDisplayNumber(profile.attributeBonuses.maxHpPercent * 100)}%
          </span>
          <span>
            <Shield size={15} />
            {formatDisplayNumber(profile.attributeBonuses.defensePercent * 100)}%
          </span>
          <span>
            <Sword size={15} />
            {formatDisplayNumber(profile.attributeBonuses.attackPercent * 100)}%
          </span>
        </div>
      </header>
      {independentWork ? (
        <section className="life-work-overview">
          <button
            className="life-work-entry"
            onClick={() => setWorkWindowOpen(true)}
            aria-haspopup="dialog"
          >
            <LifeProfessionIcon id={selected} size={28} />
            <span>
              <strong>{profession?.name}</strong>
              <small>
                Lv.{profession?.level} ·{" "}
                {profession ? lifeProfessionCompactBenefit(profession) : ""}
              </small>
            </span>
            <ChevronRight size={18} />
          </button>
          {profile.activity && (
            <div className="life-job-summary">
              <span>
                <strong>{activityProfession?.name}</strong>
                <small>{activityStatus}</small>
              </span>
              <div className="life-active-actions">
                <button className="primary-command compact" disabled={busy} onClick={onClaim}>
                  <PackageOpen size={16} />
                  领取
                </button>
                <button className="secondary-command compact" disabled={busy} onClick={onStop}>
                  <CircleSlash size={16} />
                  停止
                </button>
              </div>
            </div>
          )}
        </section>
      ) : (
        workDetails
      )}
      {independentWork && workWindowOpen && (
        <DetailWindow
          className="life-work-window"
          labelledBy="life-work-title"
          onClose={() => setWorkWindowOpen(false)}
          header={
            <h2 id="life-work-title">
              <LifeProfessionIcon id={selected} />
              {profession?.name}
            </h2>
          }
        >
          {workDetails}
        </DetailWindow>
      )}
      <div className="life-layout">
        <nav className="life-professions" aria-label="生活职业">
          {profile.professions.map((entry) => (
            <button
              key={entry.id}
              className={selected === entry.id ? "is-selected" : ""}
              aria-pressed={selected === entry.id}
              aria-haspopup={
                entry.id === "enhancement" || entry.id === "research" ? "dialog" : undefined
              }
              onClick={() => {
                setSelected(entry.id);
                setWorkWindowOpen(entry.id === "enhancement" || entry.id === "research");
              }}
            >
              <span className="life-profession-icon">
                <LifeProfessionIcon id={entry.id} />
              </span>
              <span>
                <strong>{entry.name}</strong>
                <small>
                  Lv.{entry.level} · {lifeProfessionCompactBenefit(entry)}
                </small>
              </span>
              <i>
                <em
                  style={{
                    width: `${Math.min(100, entry.nextLevelExperience > 0 ? (entry.levelExperience / entry.nextLevelExperience) * 100 : 100)}%`,
                  }}
                />
              </i>
            </button>
          ))}
        </nav>
      </div>
    </section>
  );
}
